import { useState, useEffect, useRef, memo } from 'react'
import { clsx } from 'clsx'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { Bell, Paperclip, GripVertical, ListChecks, Trash2, Check, FileText } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { springStiff, springGentle } from '@/lib/motionConfig'
import { Checkbox } from '@/components/ui/Checkbox'
import { FlagIcon } from '@/components/ui/FlagIcon'
import { isOverdue, formatDueDateWithTime } from '@/lib/dateUtils'
import { formatOverdueBadge } from '@/lib/overdueUtils'
import { playCompletionChime } from '@/lib/celebrationSound'
import { useSettingsStore } from '@/stores/settingsStore'
import { useSwipeAction } from '@/hooks/useSwipeAction'
import { useLongPress } from '@/hooks/useLongPress'
import type { Task, TaskViewMode } from '@/lib/types'
import { getCategoryColorStyle, getCategoryBgStyle } from '@/lib/categoryColors'

// ─── Row enter/exit variants (AnimatePresence popLayout in TaskList) ───
// 풀 모션: 위로 살짝 떠오르며 진입, 삭제/완료 시 좌측으로 슬라이드 + 높이 붕괴
const rowVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: springGentle },
  exit: {
    opacity: 0,
    x: -24,
    height: 0,
    marginTop: 0,
    transition: { duration: 0.18, ease: 'easeOut' },
  },
}
// reduced-motion: 투명도만
const rowVariantsReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
}

interface TaskItemProps {
  task: Task
  categoryName?: string
  categoryColor?: string
  onToggleComplete: (id: number) => void
  onToggleFlag: (id: number) => void
  onClick: (id: number) => void
  isSelectionMode?: boolean
  isSelected?: boolean
  onSelect?: (id: number) => void
  onDelete?: (id: number) => void
  sortable?: boolean
  taskGroups?: { name: string; color: string }[]
  onEnterSelectionMode?: () => void
  viewMode?: TaskViewMode
  activeCategoryId?: number | null
  /** 키보드 네비게이션 포커스 — 외곽 링 + a11y(option role) 표시 */
  isFocused?: boolean
  /** 가상 스크롤 경로 등에서 행 enter/exit/layout 모션을 끔 (virtualizer transform 충돌 방지) */
  disableRowMotion?: boolean
}

export const TaskItem = memo(function TaskItem({
  task,
  categoryName,
  categoryColor,
  onToggleComplete,
  onToggleFlag,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  onDelete,
  sortable = false,
  taskGroups,
  onEnterSelectionMode,
  viewMode = 'standard',
  activeCategoryId,
  isFocused = false,
  disableRowMotion = false,
}: TaskItemProps) {
  const isCompleted = task.status === 'completed'
  const overdue = !isCompleted && isOverdue(task.dueDate)
  const overdueBadge = overdue ? formatOverdueBadge(task.dueDate) : null
  const hasAlarm = task.alarm?.enabled ?? false
  const hasAttachment = Boolean(task.memo)
  const taskId = task.id as number
  const completionSoundEnabled = useSettingsStore((s) => s.settings.completionSoundEnabled)
  const shouldReduceMotion = useReducedMotion()

  // Hover preview (desktop only)
  const [showPreview, setShowPreview] = useState(false)
  const [previewPos, setPreviewPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Context menu (desktop only)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)

  // Track just-completed animation
  const [justCompleted, setJustCompleted] = useState(false)
  const prevCompletedRef = useRef(isCompleted)

  useEffect(() => {
    if (isCompleted && !prevCompletedRef.current) {
      setJustCompleted(true)
      if (completionSoundEnabled) {
        playCompletionChime()
      }
      const timer = setTimeout(() => setJustCompleted(false), 500)
      return () => clearTimeout(timer)
    }
    prevCompletedRef.current = isCompleted
  }, [isCompleted, completionSoundEnabled])

  // Hover preview handlers (desktop only, fixed positioning)
  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (!window.matchMedia('(min-width: 1024px)').matches) return
    const rect = e.currentTarget.getBoundingClientRect()
    const previewWidth = 256 // w-64
    const x = rect.right + 8 + previewWidth > window.innerWidth
      ? rect.left - previewWidth - 8
      : rect.right + 8
    const y = Math.min(rect.top, window.innerHeight - 120)
    setPreviewPos({ x, y })
    hoverTimerRef.current = setTimeout(() => setShowPreview(true), 300)
  }
  const handleMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    setShowPreview(false)
  }

  // Cleanup hover timer
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    }
  }, [])

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return
    const handleClick = () => setContextMenu(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [contextMenu])

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: taskId,
    disabled: !sortable || isSelectionMode,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
  }

  // Swipe actions
  const { translateX, isSwiping, handlers: swipeHandlers } = useSwipeAction({
    onSwipeRight: () => onToggleComplete(taskId),
    onSwipeLeft: () => onDelete?.(taskId),
    disabled: isSelectionMode || sortable,
  })

  // Long press for selection mode
  const { handlers: longPressHandlers } = useLongPress({
    onLongPress: () => onEnterSelectionMode?.(),
    disabled: isSelectionMode,
  })

  // Context menu handler with viewport boundary check
  const handleContextMenu = (e: React.MouseEvent) => {
    longPressHandlers.onContextMenu(e)
    if (!window.matchMedia('(min-width: 1024px)').matches) return
    e.preventDefault()
    const menuWidth = 192 // w-48
    const menuHeight = 160
    const x = e.clientX + menuWidth > window.innerWidth ? window.innerWidth - menuWidth - 8 : e.clientX
    const y = e.clientY + menuHeight > window.innerHeight ? window.innerHeight - menuHeight - 8 : e.clientY
    setContextMenu({ x, y })
  }

  // Priority color bar — 테마/오버레이에 반응하는 우선순위 토큰 사용 (semantic-colors.css)
  // border-l-4 폭 클래스는 유지하고, 좌측 보더 색만 토큰으로 inline 지정한다.
  const priorityBorderColor = isCompleted ? undefined : 'border-l-4'
  const priorityBorderStyle: React.CSSProperties | undefined = (() => {
    if (isCompleted) return undefined
    switch (task.priority) {
      case 'high': return { borderLeftColor: 'var(--priority-high)' }
      case 'medium': return { borderLeftColor: 'var(--priority-medium)' }
      case 'low': return { borderLeftColor: 'var(--priority-low)' }
      default: return { borderLeftColor: 'var(--border-strong)' }
    }
  })()

  // 완료 순간 마이크로 셀러브레이션: 살짝 스케일 팝 (reduced-motion 시 비활성).
  // 배경 워시는 카드 자체 backgroundColor를 건드리지 않도록 별도 오버레이로 처리해
  // 완료 상태의 emerald 틴트가 lingering inline style에 덮이지 않게 한다.
  const celebrateAnimate =
    !shouldReduceMotion && justCompleted ? { scale: [1, 1.015, 1] } : undefined

  // 성공 톤 배경 워시 오버레이 (justCompleted 동안만, transform/bg 충돌 없음)
  const celebrationWash =
    !shouldReduceMotion && justCompleted ? (
      <motion.span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{ backgroundColor: 'var(--color-success-50)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.6, 0] }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    ) : null

  // 행 래퍼 모션 props — AnimatePresence(popLayout) 진입/퇴장 + 이웃 재배치.
  // 활성 드래그(@dnd-kit) 중에는 layout을 끄고(transform 충돌 방지), reduced-motion 시
  // layout 비활성 + 투명도 전용 variants로 폴백.
  // 키보드 포커스 시 외곽 링 + option role을 외부 래퍼(또는 standard view 컨테이너)에 부여.
  const rowMotionProps = {
    // 가상 스크롤 경로에서는 layout/variants를 끔 — virtualizer transform과 충돌 방지
    ...(disableRowMotion
      ? {}
      : {
          layout: shouldReduceMotion || isDragging ? false : ('position' as const),
          variants: shouldReduceMotion ? rowVariantsReduced : rowVariants,
          initial: 'hidden' as const,
          animate: 'visible' as const,
          exit: 'exit' as const,
        }),
    ...(isFocused
      ? {
          role: 'option' as const,
          'aria-selected': true as const,
          'aria-label': `${task.title} — Enter로 열기, Space로 완료 토글`,
        }
      : {}),
  }
  const focusRingClass = isFocused ? 'ring-2 ring-primary-500 rounded-xl' : undefined

  const priorityLabel = task.priority === 'high' ? '높은 우선순위'
    : task.priority === 'medium' ? '중간 우선순위'
    : task.priority === 'low' ? '낮은 우선순위'
    : undefined

  // Due within 24h warning
  const isDueSoon = (() => {
    if (isCompleted || !task.dueDate || overdue) return false
    const dueDate = new Date(task.dueDate + 'T23:59:59')
    const now = new Date()
    const diff = dueDate.getTime() - now.getTime()
    return diff > 0 && diff < 24 * 60 * 60 * 1000
  })()

  // Shared hover preview JSX (fixed positioning, used by all views)
  const hoverPreviewEl = showPreview && (
    <div
      className="fixed z-50 w-64 p-3 rounded-xl bg-surface-primary border border-base shadow-xl pointer-events-none animate-in fade-in duration-150"
      style={{ top: previewPos.y, left: previewPos.x }}
    >
      {task.memo && (
        <p className="text-meta text-caption line-clamp-2 mb-2">{task.memo}</p>
      )}
      <div className="flex items-center gap-2 text-meta text-disabled flex-wrap">
        {task.dueDate && <span>📅 {formatDueDateWithTime(task.dueDate, task.dueTime)}</span>}
        {task.priority !== 'none' && (
          <span
            className="px-1.5 py-0.5 rounded font-medium"
            style={{
              color: `var(--priority-${task.priority})`,
              backgroundColor: `var(--priority-${task.priority}-bg)`,
            }}
          >
            {task.priority === 'high' ? '높음' : task.priority === 'medium' ? '중간' : '낮음'}
          </span>
        )}
        {task.subtasks.length > 0 && (
          <span>{task.subtasks.filter(st => st.isCompleted).length}/{task.subtasks.length} 하위작업</span>
        )}
      </div>
    </div>
  )

  // Shared context menu JSX (fixed positioning, used by all views)
  const contextMenuEl = contextMenu && (
    <div
      className="fixed z-[100] w-48 py-1 rounded-xl bg-surface-primary border border-base shadow-xl"
      style={{ top: contextMenu.y, left: contextMenu.x }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setContextMenu(null); onClick(taskId) }}
        className="w-full text-left px-3 py-2 text-sm text-body hover:bg-surface-tertiary"
      >
        상세 보기
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setContextMenu(null); onToggleComplete(taskId) }}
        className="w-full text-left px-3 py-2 text-sm text-body hover:bg-surface-tertiary"
      >
        {isCompleted ? '미완료로 변경' : '완료로 변경'}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setContextMenu(null); onToggleFlag(taskId) }}
        className="w-full text-left px-3 py-2 text-sm text-body hover:bg-surface-tertiary"
      >
        {task.isFlagged ? '깃발 해제' : '깃발 설정'}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setContextMenu(null); onDelete(taskId) }}
          className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
        >
          삭제
        </button>
      )}
    </div>
  )

  // Compact view
  if (viewMode === 'compact') {
    return (
      <motion.div {...rowMotionProps} className={focusRingClass}>
      <motion.div
        ref={setNodeRef}
        style={{ ...style, ...priorityBorderStyle }}
        animate={celebrateAnimate}
        transition={springStiff}
        role="button"
        tabIndex={0}
        onClick={() => onClick(taskId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick(taskId)
          }
        }}
        {...longPressHandlers}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
        className={clsx(
          'workspace-task-surface relative flex min-h-[58px] items-center gap-1.5 px-2.5 py-1.5 rounded-xl',
          'border',
          'cursor-pointer transition-all duration-200',
          isCompleted && !justCompleted && 'opacity-50 bg-emerald-50/30 dark:bg-emerald-950/10',
          isDragging && 'opacity-50 shadow-lg z-50 scale-[1.02]',
          isSelected && 'border-primary-500 bg-accent-primary',
          priorityBorderColor,
        )}
        title={priorityLabel}
      >
        {celebrationWash}
        {isSelectionMode ? (
          <div onClick={(e) => { e.stopPropagation(); onSelect?.(taskId) }} className="flex-shrink-0">
            <Checkbox checked={isSelected} onChange={() => onSelect?.(taskId)} ariaLabel={`${task.title} 선택`} />
          </div>
        ) : (
          <div>
            <Checkbox
              checked={isCompleted}
              onChange={() => onToggleComplete(taskId)}
              ariaLabel={isCompleted ? `${task.title} 미완료로 변경` : `${task.title} 완료로 변경`}
            />
          </div>
        )}
        <span className={clsx(
          'text-sm truncate flex-1',
          isCompleted ? 'line-through text-disabled' : 'text-heading'
        )}>
          {task.title}
        </span>
        {task.dueDate && (
          <span className={clsx('text-meta flex-shrink-0', overdue ? 'text-red-500 font-medium' : 'text-disabled')}>
            {formatDueDateWithTime(task.dueDate, task.dueTime)}
          </span>
        )}
        {hoverPreviewEl}
        {contextMenuEl}
      </motion.div>
      </motion.div>
    )
  }

  // Card view
  if (viewMode === 'card') {
    return (
      <motion.div {...rowMotionProps} className={focusRingClass}>
      <motion.div
        ref={setNodeRef}
        style={{ ...style, ...priorityBorderStyle }}
        animate={celebrateAnimate}
        transition={springStiff}
        role="button"
        tabIndex={0}
        onClick={() => onClick(taskId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick(taskId)
          }
        }}
        {...longPressHandlers}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
        className={clsx(
          'workspace-task-surface relative flex flex-col gap-2.5 p-4 rounded-2xl',
          'border',
          'cursor-pointer transition-all duration-200',
          'hover:shadow-e2 hover:border-[var(--hover-border)]',
          isCompleted && !justCompleted && 'opacity-50 bg-emerald-50/30 dark:bg-emerald-950/10',
          isDragging && 'opacity-50 shadow-e3 z-50 scale-[1.02]',
          isSelected && 'border-primary-500 bg-accent-primary',
          priorityBorderColor,
          isDueSoon && !overdue && 'bg-amber-50/50 dark:bg-amber-950/20',
          overdue && 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20',
        )}
        title={priorityLabel}
      >
        {celebrationWash}
        <div className="flex items-start gap-2">
          {isSelectionMode ? (
            <div onClick={(e) => { e.stopPropagation(); onSelect?.(taskId) }} className="flex-shrink-0 mt-0.5">
              <Checkbox checked={isSelected} onChange={() => onSelect?.(taskId)} ariaLabel={`${task.title} 선택`} />
            </div>
          ) : (
            <div className="flex-shrink-0 mt-0.5">
              <Checkbox
                checked={isCompleted}
                onChange={() => onToggleComplete(taskId)}
                ariaLabel={isCompleted ? `${task.title} 미완료로 변경` : `${task.title} 완료로 변경`}
              />
            </div>
          )}
          <span className={clsx(
            'text-sm font-medium flex-1',
            isCompleted ? 'line-through text-disabled' : 'text-heading'
          )}>
            {task.title}
          </span>
          {!isSelectionMode && (
            <FlagIcon flagged={task.isFlagged} onClick={() => onToggleFlag(taskId)} />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {categoryName && task.categoryId !== activeCategoryId && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-meta font-medium"
              style={{ backgroundColor: categoryColor ? getCategoryBgStyle(categoryColor) : undefined, color: categoryColor ? getCategoryColorStyle(categoryColor) : undefined }}
            >
              {categoryName}
            </span>
          )}
          {task.dueDate && (
            <span className={clsx('text-meta', overdue ? 'text-red-500 font-medium' : 'text-disabled')}>
              {formatDueDateWithTime(task.dueDate, task.dueTime)}
            </span>
          )}
          {overdueBadge && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-meta font-bold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              {overdueBadge}
            </span>
          )}
          {task.subtasks.length > 0 && (() => {
            const done = task.subtasks.filter(st => st.isCompleted).length
            return (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-meta font-medium bg-surface-tertiary text-caption">
                <ListChecks className="w-3 h-3" />
                {done}/{task.subtasks.length}
              </span>
            )
          })()}
        </div>
        {hoverPreviewEl}
        {contextMenuEl}
      </motion.div>
      </motion.div>
    )
  }

  // Standard view (default)
  const swipeActive = isSwiping && translateX !== 0

  return (
    <motion.div
      {...rowMotionProps}
      className={clsx('relative overflow-hidden rounded-xl', focusRingClass)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Swipe background */}
      {swipeActive && (
        <div className={clsx(
          'absolute inset-0 flex items-center rounded-xl',
          translateX > 0 ? 'justify-start bg-emerald-500 pl-4' : 'justify-end bg-red-500 pr-4'
        )}>
          {translateX > 0 ? (
            <Check className="w-5 h-5 text-white" />
          ) : (
            <Trash2 className="w-5 h-5 text-white" />
          )}
        </div>
      )}

      <motion.div
        ref={setNodeRef}
        style={{
          ...style,
          ...priorityBorderStyle,
          transform: isSwiping
            ? `translateX(${translateX}px)`
            : CSS.Transform.toString(transform),
        }}
        animate={celebrateAnimate}
        transition={springStiff}
        role="button"
        tabIndex={0}
        onClick={() => onClick(taskId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick(taskId)
          }
        }}
        {...swipeHandlers}
        {...longPressHandlers}
        onContextMenu={handleContextMenu}
        className={clsx(
          'workspace-task-surface relative flex min-h-[68px] items-center gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 rounded-2xl',
          'border',
          'cursor-pointer transition-all duration-200',
          'hover:border-[var(--hover-border)] hover:shadow-e1',
          isCompleted && !justCompleted && 'opacity-50 bg-emerald-50/30 dark:bg-emerald-950/10',
          isDragging && 'opacity-50 shadow-e3 z-50 scale-[1.02]',
          isSelected && 'border-primary-500 bg-accent-primary',
          priorityBorderColor,
          isDueSoon && !overdue && 'bg-amber-50/50 dark:bg-amber-950/20',
          overdue && 'border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20'
        )}
        title={priorityLabel}
      >
        {celebrationWash}
        {/* Selection checkbox or drag handle */}
        {isSelectionMode ? (
          <div
            onClick={(e) => {
              e.stopPropagation()
              onSelect?.(taskId)
            }}
            className="flex-shrink-0"
          >
            <Checkbox checked={isSelected} onChange={() => onSelect?.(taskId)} ariaLabel={`${task.title} 선택`} />
          </div>
        ) : sortable ? (
          <div
            {...attributes}
            {...listeners}
            className="flex h-11 w-11 flex-shrink-0 cursor-grab items-center justify-center rounded-xl text-disabled hover-surface active:cursor-grabbing touch-none"
            onClick={(e) => e.stopPropagation()}
            aria-label={`${task.title} 순서 변경`}
          >
            <GripVertical className="w-4 h-4 text-disabled" />
          </div>
        ) : null}

        {/* Checkbox */}
        {!isSelectionMode && (
          <div>
            <Checkbox
              checked={isCompleted}
              onChange={() => onToggleComplete(taskId)}
              ariaLabel={isCompleted ? `${task.title} 미완료로 변경` : `${task.title} 완료로 변경`}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={clsx(
                'line-clamp-2 text-sm font-semibold leading-snug sm:line-clamp-1',
                isCompleted
                  ? 'line-through text-disabled'
                  : 'text-heading',
                justCompleted && 'animate-strikethrough'
              )}
            >
              {task.title}
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 flex-wrap">
            {/* Category badge */}
            {categoryName && task.categoryId !== activeCategoryId && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-meta font-medium"
                style={{
                  backgroundColor: categoryColor ? getCategoryBgStyle(categoryColor) : undefined,
                  color: categoryColor ? getCategoryColorStyle(categoryColor) : undefined,
                }}
              >
                {categoryName}
              </span>
            )}

            {/* Group badges (hidden on Fold) */}
            {taskGroups && taskGroups.length > 0 && (
              <span className="contents fold:hidden">
                {taskGroups.slice(0, 2).map((g, i) => (
                  <span
                    key={i}
                    className="chip chip-sm chip-outline"
                  >
                    <span
                      className="chip-dot"
                      style={{ backgroundColor: getCategoryColorStyle(g.color) }}
                    />
                    {g.name}
                  </span>
                ))}
                {taskGroups.length > 2 && (
                  <span className="text-meta text-disabled">+{taskGroups.length - 2}</span>
                )}
              </span>
            )}

            {/* Subtask progress (hidden on Fold) */}
            {task.subtasks.length > 0 && (() => {
              const done = task.subtasks.filter(st => st.isCompleted).length
              const total = task.subtasks.length
              const pct = Math.round((done / total) * 100)
              return (
                <span className={clsx(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-meta font-medium fold:hidden',
                  done === total
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                    : 'bg-surface-tertiary text-caption'
                )}>
                  <ListChecks className="w-3 h-3" />
                  {done}/{total}
                  <span className="w-8 h-1 rounded-full bg-surface-muted overflow-hidden">
                    <span className="block h-full bg-primary-500 rounded-full" style={{ width: `${pct}%` }} />
                  </span>
                </span>
              )
            })()}

            {/* Due date */}
            {task.dueDate && (
              <span
                className={clsx(
                  'text-meta',
                  overdue
                    ? 'text-red-500 font-medium'
                    : 'text-disabled'
                )}
              >
                {formatDueDateWithTime(task.dueDate, task.dueTime)}
              </span>
            )}

            {/* Overdue badge */}
            {overdueBadge && (
              <span className="badge-overdue">
                {overdueBadge}
              </span>
            )}

            {/* Icons */}
            {hasAlarm && (
              <Bell className="w-4 h-4 text-disabled flex-shrink-0" />
            )}
            {hasAttachment && (
              <span className="inline-flex items-center gap-0.5 text-disabled p-1">
                <Paperclip className="w-4 h-4 flex-shrink-0" />
              </span>
            )}
            {(task.linkedMemos?.length ?? 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 text-violet-400 p-1" title="연결된 메모">
                <FileText className="w-4 h-4 flex-shrink-0" />
              </span>
            )}
          </div>
        </div>

        {/* Flag */}
        {!isSelectionMode && (
          <FlagIcon
            flagged={task.isFlagged}
            onClick={() => onToggleFlag(taskId)}
          />
        )}
      </motion.div>

      {hoverPreviewEl}
      {contextMenuEl}
    </motion.div>
  )
})
